/**
 * **A identidade de um proc confirmado, definida UMA vez** (gate V28G.U).
 *
 * ## O problema que este ficheiro resolve
 *
 * Havia duas ligações - `connection.ts` para o duelo e `team-connection.ts`
 * para o 3x3 - e cada uma tinha a sua própria memória de deduplicação, escrita à
 * mão. Elas concordavam por coincidência, e deixaram de concordar exatamente
 * como duas cópias deixam: a do duelo foi corrigida primeiro e a do 3x3 ficou
 * para trás, a deduplicar pelo id do projétil sozinho - no MODO onde o problema
 * é maior, porque é o único que tem seis cascos.
 *
 * As duas entregam o mesmo facto a uma apresentação PARTILHADA. Uma identidade e
 * não duas.
 *
 * ## As três partes, e o que cada uma ganhou
 *
 * - **o projétil** (`i`) é a âncora, e é ele e não um contador porque sobrevive
 *   a uma hibernação da sala. Um contador reiniciado faria o cliente descartar
 *   procs novos por os julgar antigos;
 *
 * - **o efeito** (`e`) entrou porque um projétil confirma mais do que um facto.
 *   Um tiro com o Ciclone que acerta num casco com o Orbe Vampírico anuncia um
 *   corte E um sifão com o mesmo `i`, e uma chave que fosse só o id deixava cair
 *   o segundo em silêncio. O Coração Vital tornou-o inevitável em vez de
 *   ocasional: a cura é anunciada no DISPARO, com o id do projétil que a salva
 *   acabou de criar, e esse mesmo projétil ainda vai poder cortar quando aterrar;
 *
 * - **o alvo** (`q`) entrou pela razão simétrica: o mesmo efeito do mesmo
 *   projétil pode acontecer sobre cascos DIFERENTES. Um duelo não o consegue
 *   exprimir - duas cadeiras, e um payload de `a` só pode acertar em `b` - mas um
 *   3x3 tem seis, e um payload de área é a forma óbvia disso. A fronteira certa
 *   para o cobrir é esta: a ligação recebe factos autoritativos e não sabe que
 *   forma o emissor tinha, portanto uma chave sem o alvo obrigaria cada emissor
 *   futuro a lembrar-se de que a apresentação o deixaria cair.
 *
 * ## Porque não um id de evento
 *
 * Porque não existe nenhum, e inventá-lo seria acrescentar estado ao fio para
 * dizer o que ele já diz. O canal do proc carrega o que a autoridade decidiu -
 * efeito, autor, alvo, salva, tique e magnitude - e o trio é a identidade mínima
 * que esses campos já formam.
 *
 * ## O que a chave continua a recusar
 *
 * Uma REPETIÇÃO, que é tudo o que ela existe para recusar. Um frame reenviado,
 * uma sala reconstruída que re-simula um tique, um `stateFor` fora da cadência:
 * os três trazem os três campos iguais. O trio é único por construção, porque um
 * efeito é confirmado no máximo uma vez por projétil e por alvo - um projétil
 * resolve contra cada casco uma vez.
 *
 * ## O que NÃO entra na chave, e porquê
 *
 * O **tique** (`t`). Ele é o mesmo facto visto de outro instante quando a sala
 * re-simula: uma reconstrução repete o corte com um `t` diferente, e incluí-lo
 * faria a deduplicação deixar de deduplicar exatamente no caso para que foi
 * escrita.
 *
 * A **magnitude** (`h`) e a **posição** (`x`, `y`), pela mesma razão e com um
 * agravante: são valores contínuos, e uma chave que os incluísse dependeria de
 * dois `number` coincidirem ao bit entre duas simulações.
 */

/** O mínimo de um proc que a identidade lê. Nunca o frame inteiro. */
export interface PvpProcIdentity {
  /** O projétil que o trouxe. */
  readonly i: number;
  /** O efeito confirmado. Nunca a joia. */
  readonly e: string;
  /** Em quem foi confirmado. Autoritativo: o cliente não escolhe um alvo. */
  readonly q: string;
}

/**
 * A chave de deduplicação de um proc: `(projétil, efeito, alvo)`.
 *
 * Uma string e não um objeto porque o que a consome é um `Set`, e um `Set` de
 * objetos compara por referência - o que faria cada frame novo parecer um facto
 * novo, que é o oposto do que isto existe para fazer.
 *
 * Os separadores são `:` e os três campos não o contêm: `i` é um inteiro, `e` é
 * um id de efeito do domínio e `q` é um id de participante (`a1`..`b3`, ou `a` e
 * `b` no duelo). Uma colisão exigiria um id com dois pontos, que nenhuma das
 * três fontes pode produzir.
 */
export function pvpProcDedupKey(proc: PvpProcIdentity): string {
  return `${proc.i}:${proc.e}:${proc.q}`;
}
